/**
 * Globe palette and tuning constants.
 *
 * Everything here is a plain value so the style module stays pure and testable.
 * There are no font stacks, no glyph URLs and no sprite URLs anywhere in this
 * package — invariant 7 is enforced structurally, not by convention.
 */

export const COLORS = {
  /** Behind the sphere. Also the container background. */
  space: '#05070d',
  ocean: '#0d1b2a',
  land: '#28394d',
  landHover: '#33475e',
  border: '#54708f',
  coast: '#7b98b8',
  graticule: '#1d3047',
  /** Microstate dots: land-coloured so they read as tiny islands, not markers. */
  microstate: '#3a5069',
  microstateEdge: '#7b98b8',

  /**
   * The colour night is painted with, over whatever skin is beneath.
   *
   * A deep blue rather than black: Earth's night side is lit by moon and
   * airglow, and pure black reads as a hole punched in the planet rather than
   * as the far side of it.
   */
  night: '#050a16',

  correct: '#2fbf71',
  incorrect: '#e5484d',
  neutral: '#f5c451',

  /** Subdivision borders: a thinner, cooler line than a national one. */
  regionBorder: '#8fb4d8',

  pinYou: '#38bdf8',
  /**
   * THE OPPONENT'S GUESS, and it must not look like the ANSWER.
   *
   * It was `#f97316`, an orange sitting one step round the wheel from the
   * `#facc15` the target is drawn in. At pin size, behind a near-black ring,
   * that is not a distinction: reported as "the other persons guess should not
   * be yellow (same color as answer)". The reveal was showing where the real
   * capital was and where the opponent had guessed in what read as the same
   * colour, which makes the reveal say nothing.
   *
   * It was also the exact orange the HUD uses for PUSH (`--color-push`), so
   * the same swatch meant "your opponent", "the answer" and "bet it all".
   *
   * Magenta because it is the one region of the wheel this game does not
   * already use -- gold is the answer, blue is you, green and red are correct
   * and incorrect -- and because it survives red-green colour blindness, where
   * gold against orange does not.
   */
  pinOpponent: '#d946ef',
  pinTarget: '#facc15',
  /** A map-choice dot: a question, so neither the correct nor incorrect colour. */
  pinChoice: '#e9d5ff',
  pinStroke: '#04070c',
} as const;

/** Camera the globe boots to and that `resetView()` returns to. */
export const HOME_VIEW = {
  center: [10, 18] as [number, number],
  zoom: 0.9,
  bearing: 0,
  pitch: 0,
} as const;

/**
 * Fraction of the viewport's short edge the globe should span at rest.
 *
 * `HOME_VIEW.zoom` is a single fixed number, which means the globe is the same
 * 478 px disc on a 1900 px desktop and on a 375 px phone — lost in space on one,
 * overflowing on the other. Fitting it to the viewport instead is why
 * `homeZoomFor` exists; this is the only knob it takes.
 */
export const HOME_FILL_FRACTION = 0.82;

/** Portrait-phone globe diameter as a fraction of the visible viewport height. */
export const MOBILE_HOME_FILL_FRACTION = 0.94;

export const CAMERA = {
  minZoom: 0.4,
  maxZoom: 7,
  /** Globe projection plus pitch is a mess to pick on. Keep it flat. */
  maxPitch: 0,
  defaultFlyMs: 900,
  defaultOrientMs: 800,
} as const;

/** Degrees of longitude per second while idle-spinning. */
export const IDLE_SPIN_DEG_PER_SEC = 3.2;

/** A pointer that travels further than this between down and up is a drag. */
export const TAP_SLOP_PX = 10;
/** A press held longer than this is a drag/inspect, not a tap. */
export const TAP_MAX_MS = 700;

export const SNAP = {
  /** Used when the caller does not pass `snapRadiusPx`. Navigator's value. */
  defaultRadiusPx: 28,
  /** Ring spacing of the outward search. Smaller = finer, more queries. */
  ringStepPx: 4,
  /** Target arc spacing between samples on a ring. */
  arcStepPx: 5,
  /**
   * A point target beats an overlapping polygon within this many pixels.
   * Vatican sits inside Rome; without the bias Italy always wins the tap.
   *
   * DELIBERATELY NOT TIER-SCALED, and that is the interesting part. Scaling it
   * with `snapRadiusPx` sounds right — Explorer is the forgiving tier — but it
   * is backwards. The dots are on the globe at every tier, including the ones
   * Explorer's pool can never ask about, so a 28px Explorer bias would let
   * Monaco steal a 28px disc out of France from a player who is not allowed to
   * be asked for Monaco. The tier lever is `snapRadiusPx`, which controls how
   * far a MISS may be from a polygon; this one is a property of the human
   * hand, so it is a constant. 12px ≈ 6mm on a phone: half of Material's 48dp
   * minimum touch target, which is as tight as a deliberate tap gets.
   */
  microBiasPx: 12,
  microDotRadiusPx: 2.6,
  /**
   * On-screen radius, in CSS pixels, below which a country is picked as a
   * POINT instead of a polygon. See `targets.ts` — this is the whole reason
   * Luxembourg and Qatar are answerable at the default view.
   */
  subResolutionPx: 6,
  /**
   * How far a hurried thumb lands from where it aimed. Two point targets whose
   * distances to the tap differ by less than this are a coin flip, not a
   * choice, so the globe magnifies instead of guessing.
   */
  aimTolerancePx: 8,
  /**
   * How close a tap must land to a geometry-less country's dot before it beats
   * the polygon under the finger outright. Beyond it — but still inside
   * `microBiasPx` — the tap is a tie and the globe magnifies: Andorra's window
   * otherwise reaches the middle of Spain, and answering "Andorra" to a player
   * who tapped central Spain is worse than asking them to tap again.
   */
  pointCertaintyPx: 6,
  /**
   * Screen separation the magnify camera gives each contender. Two bias
   * windows side by side, so every contender ends up with a full exclusive
   * touch target and the next tap cannot be ambiguous again.
   */
  separationTargetPx: 24,
  /** A magnify always zooms at least this much, so a tap always progresses. */
  minMagnifyZoomStep: 0.75,
  /** Magnify ease duration. Short: it is inside the round clock. */
  magnifyMs: 260,
  /**
   * Radius of the static answer-mark ring, in CSS pixels.
   *
   * SCREEN SPACE, NOT WORLD SPACE, and that is the whole point of the mark: it
   * is the same size on Nauru's dot as on Brazil's polygon, so "you were right"
   * is equally visible at both ends of the pool. 13px puts the stroke just
   * outside a 24px touch target, which is where the finger just was.
   */
  markRingPx: 13,
} as const;
